import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { ClearRounded, DeleteRounded, SendRounded } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { RootState } from '../../app/store';
import DocumentInfoCard from '../../components/DocumentInfoCard';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import displayRemovedUser from '../../functions/displayRemovedUser';
import sortMembers from '../../functions/sortMembers';
import { stringAvatar } from '../../functions/stringAvatar';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { Project } from '../Project/projectSlice';
import { getProjectDocumentAsync } from '../ProjectDocument/projectDocumentSlice';
import { User } from '../UserProfile/userProfileSlice';
import {
  createProjectDocumentCustomAccessControlsAsync,
  deleteProjectDocumentCustomAccessControlAsync,
  getProjectDocumentCustomAccessControlsAsync
} from './projectDocumentCustomAccessControlSlice';

interface CustomAccessControl {
  id: number;
  userId: number;
  user?: User;
  default: boolean;
}

export default function DocumentShare() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const params = useParams<{ docId: string }>();
  const docId = Number.parseInt(params.docId);

  const { users } = useSelector((state: RootState) => state.projectRegisters);
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { data: projectDocumentCustomAccessControls } = useSelector(
    (state: RootState) => state.projectDocumentCustomAccessControls
  );

  const [defaultAccessControls, setDefaultAccessControls] = useState<
    CustomAccessControl[]
  >([]);
  const [customAccessControls, setCustomAccessControls] = useState<
    CustomAccessControl[]
  >([]);
  const [creatorAccessControls, setCreatorAccessControls] = useState<
    CustomAccessControl[]
  >([]);

  const [newCustomUsers, setNewCustomUsers] = useState<User[]>([]);
  const [deleteCustomAccessControl, setDeleteCustomAccessControl] =
    useState<CustomAccessControl | null>(null);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Compose final ordered list
  const renderAccessControls: CustomAccessControl[] = [
    ...sortMembers<CustomAccessControl>(creatorAccessControls),
    ...sortMembers<CustomAccessControl>(
      defaultAccessControls.filter(
        (accessControl) =>
          accessControl.userId !== selectedProjectDocument?.documentCreatorId
      )
    ),
    ...sortMembers<CustomAccessControl>(
      customAccessControls.filter(
        (accessControl) =>
          accessControl.userId !== selectedProjectDocument?.documentCreatorId
      )
    )
  ];

  useEffect(() => {
    if (!selectedProject || !selectedProjectDocument) return;

    const accessControlId = selectedProjectDocument.accessControlId;
    const documentCreatorId = selectedProjectDocument.documentCreatorId;

    function getDefaultAccessControls(project: Project): CustomAccessControl[] {
      return (
        project.users
          .filter((user) =>
            user.accessControls.some((ac) => ac.id === accessControlId)
          )
          .map((projectUser) => {
            const matchedUser = users.find(
              (user) => user.id === projectUser.userId
            );
            return {
              id: projectUser.userId,
              userId: projectUser.userId,
              user: matchedUser,
              default: true
            };
          }) ?? []
      );
    }

    function getCustomAccessControls(): CustomAccessControl[] {
      return projectDocumentCustomAccessControls.map((ac) => {
        const matchedUser = users.find((user) => user.id === ac.userId);
        return {
          id: ac.id,
          userId: ac.userId,
          user: matchedUser,
          default: false
        };
      });
    }

    function getCreatorAccessControl(
      customAccessControls: CustomAccessControl[]
    ): CustomAccessControl[] {
      const creator = users.find((user) => user.id === documentCreatorId);
      const creatorAccess = customAccessControls.find(
        (ac) => ac.userId === creator?.id
      );
      return [
        {
          id: creatorAccess?.id ?? -1,
          userId: documentCreatorId,
          user: creator,
          default: creatorAccess?.default ?? true
        }
      ];
    }

    const customControls = getCustomAccessControls();
    const customUserIds = new Set(customControls.map((c) => c.userId));
    const defaultControls = getDefaultAccessControls(selectedProject).filter(
      (control) => !customUserIds.has(control.userId)
    );
    const creatorControl = getCreatorAccessControl(customControls);

    setCustomAccessControls(customControls);
    setDefaultAccessControls(defaultControls);
    setCreatorAccessControls(creatorControl);
  }, [
    selectedProjectDocument,
    selectedProject,
    users,
    projectDocumentCustomAccessControls
  ]);

  useEffect(() => {
    dispatch(getProjectDocumentAsync(docId));
    dispatch(getProjectDocumentCustomAccessControlsAsync(docId));
  }, []);

  function handleDelete(deleteCustomAccessControl: CustomAccessControl) {
    setDeleteCustomAccessControl(deleteCustomAccessControl);
    setOpenDelete(true);
  }

  function confirmDelete() {
    if (deleteCustomAccessControl) {
      dispatch(
        deleteProjectDocumentCustomAccessControlAsync({
          projectDocumentCustomAccessControlId: deleteCustomAccessControl.id,
          projectDocumentId: docId
        })
      );
      setOpenDelete(false);
    }
  }

  async function onNewCustomMember(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      const createCustomAccessControl = {
        projectDocumentId: docId,
        userIds: newCustomUsers.map((newCustomUser) => newCustomUser.id)
      };
      const result = await dispatch(
        createProjectDocumentCustomAccessControlsAsync(
          createCustomAccessControl
        )
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('documentShare.messages.usersAdded')
        })
      );
      setNewCustomUsers([]);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="shareDocument"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('documentShare.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && (
            <DocumentInfoCard document={selectedProjectDocument} />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('documentShare.subtitle')}
            </Typography>
            <Divider className={classes.divider} />
            <form onSubmit={onNewCustomMember}>
              <Autocomplete
                className={classes.fullWidth}
                id="new-custom-user"
                size="small"
                multiple
                options={users.filter(
                  (user) =>
                    !customAccessControls.some(
                      (customAccessControlUser) =>
                        customAccessControlUser.userId === user.id
                    )
                )}
                getOptionLabel={(option) =>
                  `${option?.firstName} ${option?.lastName}`
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={`${option?.firstName} ${option?.lastName}`}
                      {...getTagProps({ index })}
                      variant="outlined"
                      size="small"
                      deleteIcon={<ClearRounded />}
                    />
                  ))
                }
                value={newCustomUsers}
                getOptionSelected={(option, value) => option.id === value?.id}
                onChange={(event, newValue) => setNewCustomUsers(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      newCustomUsers.length === 0 ? t('documentShare.placeholders.selectUser') as string : ''
                    }
                    required={newCustomUsers.length === 0}
                  />
                )}
              />
              <Collapse in={newCustomUsers.length > 0}>
                <div className={classes.collapseButton}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SendRounded />}
                    fullWidth
                    disabled={loading}
                  >
                    {t('documentShare.actions.share')}
                  </Button>
                  <Button
                    className={classes.marginLeft}
                    fullWidth
                    onClick={() => setNewCustomUsers([])}
                  >
                    {t('documentShare.actions.cancel')}
                  </Button>
                </div>
                <Divider />
              </Collapse>
            </form>
            <div className={classes.paddingTop}>
              {renderAccessControls.length > 0 ? (
                renderAccessControls.map((renderUser, index) => {
                  const removedUser = !renderUser.user;
                  return (
                    <div
                      className={clsx(
                        classes.flexAlignCenter,
                        classes.marginTopBottom
                      )}
                      key={index}
                    >
                      <Avatar
                        className={classes.avatar}
                        {...stringAvatar(
                          displayRemovedUser(renderUser.userId, renderUser.user)
                        )}
                      />
                      <div className={clsx(classes.username, classes.flexGrow)}>
                        <Typography
                          color={removedUser ? 'textSecondary' : 'textPrimary'}
                        >
                          {displayRemovedUser(
                            renderUser.userId,
                            renderUser.user
                          )}
                        </Typography>
                        {renderUser.userId ===
                          selectedProjectDocument?.documentCreatorId && (
                          <Typography variant="caption" color="textSecondary">
                            {t('documentShare.labels.documentCreator')}
                          </Typography>
                        )}
                      </div>
                      {!renderUser.default ? (
                        <Tooltip title={t('documentShare.tooltips.remove') as string} arrow>
                          <IconButton
                            className={classes.marginLeft}
                            size="small"
                            onClick={() => handleDelete(renderUser)}
                          >
                            <DeleteRounded />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip
                          label={t('documentShare.labels.defaultAccess')}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className={classes.fileNotFound}>{t('documentShare.messages.userNotFound')}</div>
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={openDelete}
        onClose={() => setOpenDelete(false)}
      >
        <DialogTitle id="title">{t('documentShare.confirmDelete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="description">
            {t('documentShare.confirmDelete.message', {
              userName: deleteCustomAccessControl && deleteCustomAccessControl.user
                ? `${deleteCustomAccessControl.user.firstName} ${deleteCustomAccessControl.user.lastName}`
                : t('documentShare.removedUser')
            })}
          </DialogContentText>
        </DialogContent>
        <div className={classes.flexAction}>
          <Button
            className={classes.redColor}
            fullWidth
            color="primary"
            variant="contained"
            onClick={confirmDelete}
            disabled={Boolean(
              deleteCustomAccessControl && deleteCustomAccessControl.id <= 0
            )}
          >
            {t('documentShare.actions.remove')}
          </Button>
          <Button fullWidth onClick={() => setOpenDelete(false)}>
            {t('documentShare.actions.cancel')}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2, 2, 1, 2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fileNotFound: {
      padding: theme.spacing(6, 2),
      textAlign: 'center'
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    },
    marginTopBottom: {
      margin: theme.spacing(1, 0)
    },
    paddingTop: {
      paddingTop: theme.spacing(1)
    },
    flexGrow: {
      flexGrow: 1
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexAction: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1)
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column-reverse',
        width: `calc(100% - ${theme.spacing(2)}px)`,
        '& > *': {
          marginTop: 0
        }
      }
    },
    redColor: {
      backgroundColor: theme.palette.error.main,
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: theme.palette.error.dark
      }
    },
    fullWidth: {
      width: '100%'
    },
    collapseButton: {
      display: 'flex',
      paddingTop: 16,
      paddingBottom: 16
    },
    avatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      marginRight: theme.spacing(1)
    },
    username: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(-1),
      '& > *': { lineHeight: '120%' }
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    avatarDisabled: {
      backgroundColor: theme.palette.text.disabled
    }
  })
);
